"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import rolesService from "@/services/rolesService";

interface ISMSCommittee {
  id: string;
  roleName: string;
}

interface ISMSCommitteeComboboxProps {
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  singleSelect?: boolean;
  onCommitteeSelect?: (committeeId: string) => void;
  onCommitteeDeselect?: (committeeId: string) => void;
  committees?: ISMSCommittee[];
  fetchCommittees?: () => Promise<ISMSCommittee[]>;
}

export function ISMSCommitteeCombobox({
  selected,
  setSelected,
  placeholder = "Select a committee...",
  className,
  fullWidth = false,
  singleSelect = false,
  onCommitteeSelect,
  onCommitteeDeselect,
  committees: propCommittees,
  fetchCommittees: propFetchCommittees,
}: ISMSCommitteeComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [committees, setCommittees] = React.useState<ISMSCommittee[]>(propCommittees || []);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [loading, setLoading] = React.useState(!propCommittees?.length);
  const [error, setError] = React.useState<Error | null>(null);

  // Use propCommittees if provided, otherwise use internal state
  const displayCommittees = propCommittees || committees;

  React.useEffect(() => {
    const fetchCommitteesData = async () => {
      if (propFetchCommittees) {
        try {
          setLoading(true);
          const fetchedCommittees = await propFetchCommittees();
          setCommittees(fetchedCommittees);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to fetch committees'));
          console.error('Error fetching committees:', err);
        } finally {
          setLoading(false);
        }
      } else if (!propCommittees?.length) {
        // Default fetch if no committees provided
        try {
          setLoading(true);
          const data = await rolesService.getISMSRoles();
          const committeeList = data.ismsCommitteeRoles.map(committee => ({
            id: committee.id,
            roleName: committee.roleName
          }));
          setCommittees(committeeList);
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to fetch committees'));
          console.error('Error fetching committees:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCommitteesData();
  }, [propFetchCommittees, propCommittees]);

  const handleSelect = (committeeId: string) => {
    let newSelected: string[];
    
    if (singleSelect) {
      newSelected = selected[0] === committeeId ? [] : [committeeId];
    } else {
      newSelected = selected.includes(committeeId)
        ? selected.filter((id) => id !== committeeId)
        : [...selected, committeeId];
    }
    
    setSelected(newSelected);
    
    if (singleSelect && newSelected.length === 0) {
      onCommitteeDeselect?.(committeeId);
    } else if (singleSelect) {
      onCommitteeSelect?.(newSelected[0]);
    } else if (selected.includes(committeeId)) {
      onCommitteeDeselect?.(committeeId);
    } else {
      onCommitteeSelect?.(committeeId);
    }
    
    if (singleSelect) {
      setOpen(false);
    }
  };

  const handleRemove = (committeeId: string) => {
    const newSelected = selected.filter((id) => id !== committeeId);
    setSelected(newSelected);
    onCommitteeDeselect?.(committeeId);
  };

  const filteredCommittees = displayCommittees.filter((committee) =>
    committee.roleName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (error) {
    return <div className="text-destructive">Error loading committees</div>;
  }

  return (
    <div className={cn("space-y-2", className, { 'w-full': fullWidth })}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            role="combobox"
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-controls="committee-combobox-content"
            className={cn(
              "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              {
                'text-muted-foreground': selected.length === 0,
              },
              className
            )}
          >
            <div 
              className="flex flex-wrap gap-1 max-h-20 overflow-y-auto"
              onClick={() => setOpen(true)}
            >
              {selected.length > 0 ? (
                selected.map((committeeId) => {
                  const committee = displayCommittees.find((c) => c.id === committeeId);
                  if (!committee) return null;
                  
                  return (
                    <HoverCard key={committeeId}>
                      <HoverCardTrigger asChild>
                        <div className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm cursor-help">
                          <div className="flex items-center gap-2">
                            <span className="max-w-[200px] truncate text-sm">{committee.roleName}</span>
                          </div>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-label={`Remove ${committee.roleName}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(committeeId);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemove(committeeId);
                              }
                            }}
                            className="opacity-50 hover:opacity-100 flex items-center justify-center h-4 w-4 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                          >
                            <X className="h-3 w-3" />
                          </div>
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80">
                        <div className="space-y-2">
                          <h4 className="font-medium leading-none">{committee.roleName}</h4>
                          <p className="text-sm text-muted-foreground">
                            ID: {committee.id}
                          </p>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  );
                })
              ) : (
                <span className="text-sm">{placeholder}</span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search committees..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandEmpty>No committees found.</CommandEmpty>
            <CommandGroup>
              <CommandList>
                {loading ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Loading committees...
                  </div>
                ) : filteredCommittees.length > 0 ? (
                  filteredCommittees.map((committee) => (
                    <CommandItem
                      key={committee.id}
                      value={committee.roleName}
                      onSelect={() => handleSelect(committee.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center">
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selected.includes(committee.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span>{committee.roleName}</span>
                      </div>
                    </CommandItem>
                  ))
                ) : (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No committees available
                  </div>
                )}
              </CommandList>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
